import { Icon, useSnackbar } from '@aibees/shared/components';
import { classnames, newDateFormat } from '@aibees/shared/utils';
import { Badge, Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { differenceInCalendarDays } from 'date-fns';
import { memo } from 'react';
import { useNotificationNewStatus } from '../../hooks/use-notification-status';
import * as Styled from './styles';
import * as SX from './sx';
import type { NotificationDetailProps as Props } from './types';
import { formatNotificationText } from './utils';

export const NotificationDetail = memo(
  ({
    className,
    item,
    notificationKey,
    body,
    metadata,
    actions,
    title,
    icon,
    badgeInfo,
    progressbar,
    children,
    divider,
    onClick,
  }: Props) => {
    const { closeSnackbar } = useSnackbar();
    const { isNewlyAdded } = useNotificationNewStatus(item?.id, item?.status);

    const handleClick = () => {
      if (onClick) {
        onClick();
      }
    };
    return (
      <Styled.Wrapper
        className={classnames('NotificationDetail', className)}
        sx={SX.root}
        onClick={handleClick}
      >
        <Stack sx={SX.wrapper}>
          <Badge
            badgeContent={
              <Icon
                name={badgeInfo?.icon || 'questionMark'}
                sxx={badgeInfo?.iconSx}
                size={14}
              />
            }
            color={badgeInfo?.color}
            size={16}
          >
            <Stack sx={SX.iconStatusWrapper}>
              <Icon name={icon || 'questionMark'} size={24} sxx={SX.icon} />
            </Stack>
          </Badge>

          <Stack sx={SX.content}>
            <Stack sx={SX.header}>
              <Stack sx={SX.titleWrapper}>
                <Typography sx={SX.title}>
                  {formatNotificationText(
                    title ?? '',
                    false,
                    item?.message?.metadata
                  )}
                </Typography>

                {isNewlyAdded && (
                  <Chip
                    label="New"
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                )}
              </Stack>

              {actions}
              {!!progressbar && (
                <Icon
                  name="close"
                  size={16}
                  sxx={SX.closeIcon}
                  onClick={() => closeSnackbar(notificationKey)}
                />
              )}
            </Stack>

            <Stack sx={SX.body}>
              <Typography
                component="div"
                dangerouslySetInnerHTML={{
                  __html: item?.message?.body
                    ? formatNotificationText(
                        item?.message?.body,
                        true,
                        item?.message?.metadata
                      )
                    : (body as string),
                }}
                sx={SX.message}
              />

              {metadata}
            </Stack>
            {item && item.message?.created && (
              <Box sx={SX.dateWrapper}>
                <Typography sx={SX.date}>
                  {differenceInCalendarDays(
                    new Date(),
                    new Date(item?.message?.created || '')
                  )}
                  d Ago
                </Typography>
                <Typography sx={SX.date}> • </Typography>
                <Typography sx={SX.date}>
                  {newDateFormat(
                    new Date(item?.message?.created || ''),
                    'HH:mm'
                  )}
                </Typography>
              </Box>
            )}
          </Stack>

          {children}
        </Stack>
        {divider && <Divider variant="fullWidth" sx={SX.divider} />}
        {progressbar}
      </Styled.Wrapper>
    );
  }
);

NotificationDetail.displayName = 'NotificationDetail';
